import React, {useCallback, useEffect} from 'react'
import PropTypes from 'prop-types'
import {
  Alert,
  Col,
  CustomerDialogButtonRow,
  H3,
  InputContainer,
  Row,
  SelectPure,
  TextAreaPure,
  WarningAlert,
  withInputContainer,
} from '@modifi/ui'
import {BankTransactionTagCategory} from '@modifi/enums'
import {Form, FormActionsContext, FormInput, SubmitBehavior} from '@modifi/plugin-forms'
import {useTranslation} from '@modifi/plugin-i18n'
import {InferPropTypes} from '@modifi/typescript-utils'
import {useAbility} from '../../../../constants/permissions'
import {TRANSACTION} from '../../../../constants/section'

const SelectPureWithInputContainer = withInputContainer(SelectPure)

const TextAreaWithContainer = (props: any) => (
  <InputContainer inputComponent={TextAreaPure} {...props} />
)

const transactionCategoryOptions = (t: any): {value: string; label: string}[] =>
  Object.values(BankTransactionTagCategory).map(value => ({
    value,
    label: t(`transaction-status.${value}`),
  }))

const propTypes = {
  // from parent
  params: PropTypes.shape({
    tag: PropTypes.shape({
      additionalComment: PropTypes.string.isRequired,
      transactionCategory: PropTypes.string.isRequired,
    }).isRequired,
    id: PropTypes.number,
  }).isRequired,

  // mapStateToProps
  isTagging: PropTypes.bool.isRequired,
  tagError: PropTypes.string,

  // mapDispatchToProps
  tagTransaction: PropTypes.func.isRequired,
  cleanupTag: PropTypes.func.isRequired,
  fetchTransaction: PropTypes.func.isRequired,
  handleClose: PropTypes.func.isRequired,
}

const defaultProps = {
  tagError: null,
}

export type EditTransactionDialogProps = InferPropTypes<typeof propTypes, typeof defaultProps>

const EditTransactionDialog: React.FC<EditTransactionDialogProps> = ({
  tagTransaction,
  params: {tag, id},
  fetchTransaction,
  handleClose,
  isTagging,
  tagError,
  cleanupTag,
}) => {
  const {t} = useTranslation('moneta')
  useEffect(() => () => cleanupTag(), [cleanupTag])

  const ability = useAbility()
  const isAllowed = ability.can('create', TRANSACTION)

  const handleSubmit = useCallback(
    async (changes: Record<string, any>) => {
      try {
        await tagTransaction({
          transactionId: id,
          changes: {
            transactionCategory: changes?.transactionCategory ?? tag?.transactionCategory,
            additionalComment: changes?.additionalComment ?? tag?.additionalComment,
          },
        })
      } catch (error) {
        // do nothing
        return false
      }
      fetchTransaction(id)
      return handleClose()
    },
    [
      fetchTransaction,
      handleClose,
      id,
      tag?.additionalComment,
      tag?.transactionCategory,
      tagTransaction,
    ]
  )

  const maxLengthMessage = useCallback(
    ({maxLength}: {maxLength: number}) =>
      t('edit-transaction.dialog.comment-max-length.warning', {maxLength}),
    [t]
  )

  return (
    <>
      {!isAllowed && (
        <Row mt={30} mb={10}>
          <Col col={10} offset={1}>
            <WarningAlert>{t('warning.alert.permission-denied')}</WarningAlert>
          </Col>
        </Row>
      )}
      <Row justifyContent="center">
        <Col auto>
          <H3 my={40}>{t('edit-transaction.dialog.tag.header')}</H3>
        </Col>
      </Row>

      <Form
        onSubmit={handleSubmit}
        submitBehavior={SubmitBehavior.SUBMIT_DISABLED_UNLESS_VALID}
        onDiscard={handleClose}
        initialValues={tag}
      >
        <Row>
          <Col col={8} offset={2}>
            <FormInput
              inputComponent={SelectPureWithInputContainer}
              name="transactionCategory"
              label={t('edit-transaction.dialog.status.label')}
              collapse
              noBlur
              options={transactionCategoryOptions(t)}
              oneOf={transactionCategoryOptions(t).map(({value}) => value)}
              oneOfMessage="tr:error.one-of"
            />
          </Col>
        </Row>
        <Row mt={40}>
          <Col col={8} offset={2}>
            <FormInput
              name="additionalComment"
              label={t('edit-transaction.dialog.comment.label')}
              inputComponent={TextAreaWithContainer}
              rows={5}
              maxLength={100}
              maxLengthMessage={maxLengthMessage}
            />
          </Col>
        </Row>
        {tagError && (
          <Row mt={30} mb={10}>
            <Col col={8} offset={2}>
              <Alert type="danger">{tagError}</Alert>
            </Col>
          </Row>
        )}
        <Row>
          <Col col={8} offset={2}>
            <FormActionsContext.Consumer>
              {ctx => (
                <CustomerDialogButtonRow
                  canSubmit={
                    ctx.canSubmit &&
                    ctx.hasChanges &&
                    !ctx.validationPending &&
                    !isTagging &&
                    isAllowed
                  }
                  onSubmitClick={ctx.submit}
                  submitText={t('edit-transaction.dialog.submit.cta')}
                  hasCancel
                  cancelText={t('edit-transaction.dialog.cancel.cta')}
                  onCancelClick={ctx.discard}
                  loading={isTagging}
                  leftSpacing={0}
                  rightSpacing={0}
                />
              )}
            </FormActionsContext.Consumer>
          </Col>
        </Row>
      </Form>
    </>
  )
}

EditTransactionDialog.propTypes = propTypes
EditTransactionDialog.defaultProps = defaultProps

export default EditTransactionDialog
